import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/**
 * Loads Google Analytics 4 (gtag.js) if `ga4_measurement_id` is set in settings.
 * Sends a page_view event on every SPA route change.
 * Also exposes window.trackBookingConversion(value) helper for booking success events.
 */
export default function GA4Loader() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__ga4Loaded) return;

    api
      .get("/settings")
      .then((r) => {
        const id = (r.data?.ga4_measurement_id || "").trim();
        if (!id || !/^G-[A-Z0-9]+$/i.test(id)) return;

        // Load gtag script
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(s);

        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag("js", new Date());
        gtag("config", id, { send_page_view: false });
        window.__ga4Id = id;
        window.__ga4Loaded = true;

        // Fire initial page_view
        gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
        });

        // Expose booking conversion helper
        window.trackBookingConversion = (value = 0, currency = "IDR") => {
          try {
            gtag("event", "booking_success", {
              currency,
              value: Number(value) || 0,
            });
          } catch (e) {}
        };
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire page_view on route change
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.__ga4Loaded || !window.gtag || !window.__ga4Id) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
    });
  }, [location.pathname, location.search]);

  return null;
}
